/*
 * HappyAlert — public imperative API (React Native)
 * ------------------------------------------------------------------
 * One reusable premium alert for the whole app. Import anywhere:
 *
 *     import HappyAlert from '../components/HappyAlert';
 *
 *     HappyAlert.success('Mentve', 'A változások elmentve.');
 *     HappyAlert.error('Hiba', 'Nem sikerült menteni.');
 *
 *     const ok = await HappyAlert.confirm({
 *         title: 'Biztos?',
 *         message: 'Ez a művelet nem vonható vissza.',
 *         confirmText: 'Törlés',
 *         destructive: true,
 *     });
 *     if (ok) { ... }
 */

import HappyAlertController from './controller';
import i18n from '../../../i18n';

const present = (config) => HappyAlertController.show(config);

const HappyAlert = {
    /** Low-level: pass a full config { type, title, message, buttons, ... }. */
    show: present,

    success: (title, message, options = {}) =>
        present({ type: 'success', title, message, ...options }),

    error: (title, message, options = {}) =>
        present({ type: 'error', title, message, ...options }),

    warning: (title, message, options = {}) =>
        present({ type: 'warning', title, message, ...options }),

    info: (title, message, options = {}) =>
        present({ type: 'info', title, message, ...options }),

    /**
     * Confirmation dialog. Resolves true (confirmed) / false (cancelled or dismissed).
     */
    confirm: ({
        title,
        message,
        confirmText = i18n.t('happy_alert_confirm_yes'),
        cancelText = i18n.t('happy_alert_confirm_cancel'),
        destructive = false,
        type = destructive ? 'warning' : 'question',
    } = {}) =>
        new Promise((resolve) => {
            present({
                type,
                title,
                message,
                buttons: [
                    { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
                    {
                        text: confirmText,
                        style: destructive ? 'destructive' : 'default',
                        onPress: () => resolve(true),
                    },
                ],
                onDismiss: () => resolve(false),
            });
        }),

    /**
     * "Unsaved changes — leave this screen?" confirmation. Like {@link confirm}
     * but renders the dedicated branded leave-screen Lottie and defaults to a
     * destructive "leave / discard" affirmative. Resolves true (leave) / false
     * (stay).
     *
     * @param {Object} [options] { title, message, confirmText, cancelText }.
     * @returns {Promise<boolean>}
     */
    confirmExit: ({
        title,
        message,
        confirmText = i18n.t('happy_alert_exit_yes'),
        cancelText = i18n.t('happy_alert_exit_stay'),
    } = {}) =>
        new Promise((resolve) => {
            present({
                type: 'confirmExit',
                title,
                message,
                buttons: [
                    { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
                    { text: confirmText, style: 'destructive', onPress: () => resolve(true) },
                ],
                onDismiss: () => resolve(false),
            });
        }),

    hide: () => HappyAlertController.hide(),
};

export default HappyAlert;
